import React, { useState, useRef, useEffect } from "react";
import { Box, Button, IconButton, Typography, LinearProgress, Tooltip } from "@mui/material";
import MicIcon from "@mui/icons-material/Mic";
import StopIcon from "@mui/icons-material/Stop";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import GraphicEqIcon from "@mui/icons-material/GraphicEq";

interface VoiceRecorderProps {
  onAudioRecorded: (blob: Blob | null, duration: number) => void;
  maxDurationSeconds?: number; // Mặc định 120 giây (2 phút)
}

export const VoiceRecorder: React.FC<VoiceRecorderProps> = ({ onAudioRecorded, maxDurationSeconds = 120 }) => {
  const [isRecording, setIsRecording] = useState(false);
  const [recordingSeconds, setRecordingSeconds] = useState(0);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [audioDuration, setAudioDuration] = useState(0);
  const [permissionError, setPermissionError] = useState<string | null>(null);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const audioPlayerRef = useRef<HTMLAudioElement | null>(null);

  // Dọn dẹp URL khi unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (audioUrl) URL.revokeObjectURL(audioUrl);
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
        mediaRecorderRef.current.stop();
      }
    };
  }, [audioUrl]);

  const formatTime = (secs: number) => {
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return `${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
  };

  const startRecording = async () => {
    setPermissionError(null);
    audioChunksRef.current = [];

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, {
          type: "audio/webm",
        });
        const url = URL.createObjectURL(audioBlob);
        setAudioUrl(url);
        setAudioDuration(recordingSeconds);
        onAudioRecorded(audioBlob, recordingSeconds);

        // Tắt toàn bộ track mic
        stream.getTracks().forEach((track) => track.stop());
      };

      mediaRecorder.start(200);
      setIsRecording(true);
      setRecordingSeconds(0);

      // Đếm giây & tự động dừng khi chạm maxDurationSeconds
      timerRef.current = setInterval(() => {
        setRecordingSeconds((prev) => {
          if (prev + 1 >= maxDurationSeconds) {
            stopRecording();
            return maxDurationSeconds;
          }
          return prev + 1;
        });
      }, 1000);
    } catch (err: any) {
      console.error("Mic access error:", err);
      setPermissionError("Không thể truy cập Microphone. Vui lòng cấp quyền trong trình duyệt.");
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      mediaRecorderRef.current.stop();
    }
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
    setIsRecording(false);
  };

  const handleDeleteAudio = () => {
    if (audioUrl) {
      URL.revokeObjectURL(audioUrl);
    }
    setAudioUrl(null);
    setAudioDuration(0);
    setRecordingSeconds(0);
    setIsPlaying(false);
    onAudioRecorded(null, 0);
  };

  const togglePlayAudio = () => {
    if (!audioPlayerRef.current || !audioUrl) return;

    if (isPlaying) {
      audioPlayerRef.current.pause();
      setIsPlaying(false);
    } else {
      audioPlayerRef.current.play();
      setIsPlaying(true);
    }
  };

  const progressPercent = (recordingSeconds / maxDurationSeconds) * 100;

  return (
    <Box
      sx={{
        p: 2,
        borderRadius: 2.5,
        border: "1px solid",
        borderColor: isRecording ? "error.main" : "divider",
        backgroundColor: isRecording ? "rgba(239, 68, 68, 0.04)" : "background.paper",
        transition: "all 0.2s ease-in-out",
      }}
    >
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
        <Box display="flex" alignItems="center" gap={1}>
          <GraphicEqIcon color={isRecording ? "error" : "action"} fontSize="small" />
          <Typography variant="subtitle2" fontWeight={700}>
            Ghi âm mô tả sự cố (Tối đa 2 phút)
          </Typography>
        </Box>
        <Typography
          variant="caption"
          sx={{
            fontWeight: 700,
            color: isRecording ? "error.main" : "text.secondary",
          }}
        >
          {isRecording
            ? `${formatTime(recordingSeconds)} / ${formatTime(maxDurationSeconds)}`
            : audioUrl
              ? `Thời lượng: ${formatTime(audioDuration || recordingSeconds)}`
              : "Chưa ghi âm"}
        </Typography>
      </Box>

      {permissionError && (
        <Typography variant="caption" color="error" display="block" mb={1}>
          {permissionError}
        </Typography>
      )}

      {/* Khi đang ghi âm */}
      {isRecording && (
        <Box mb={2}>
          <LinearProgress
            variant="determinate"
            value={progressPercent}
            color="error"
            sx={{ height: 6, borderRadius: 3, mb: 1.5 }}
          />
          <Box display="flex" alignItems="center" justifyContent="center" gap={2}>
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: "50%",
                backgroundColor: "error.main",
                animation: "pulse 1s infinite",
                "@keyframes pulse": {
                  "0%": { opacity: 1, transform: "scale(1)" },
                  "50%": { opacity: 0.4, transform: "scale(1.2)" },
                  "100%": { opacity: 1, transform: "scale(1)" },
                },
              }}
            />
            <Typography variant="body2" color="error.main" fontWeight={600}>
              Đang ghi âm voice... ({formatTime(maxDurationSeconds - recordingSeconds)} còn lại)
            </Typography>
            <Button
              variant="contained"
              color="error"
              size="small"
              startIcon={<StopIcon />}
              onClick={stopRecording}
              sx={{ borderRadius: 2, textTransform: "none", fontWeight: 700 }}
            >
              Dừng ghi
            </Button>
          </Box>
        </Box>
      )}

      {/* Khi đã ghi âm xong và có file audio */}
      {!isRecording && audioUrl && (
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          sx={{
            p: 1.2,
            borderRadius: 2,
            backgroundColor: "action.hover",
          }}
        >
          <audio ref={audioPlayerRef} src={audioUrl} onEnded={() => setIsPlaying(false)} />
          <Box display="flex" alignItems="center" gap={1}>
            <IconButton
              size="small"
              color="primary"
              onClick={togglePlayAudio}
              sx={{
                backgroundColor: "primary.main",
                color: "white",
                "&:hover": { backgroundColor: "primary.dark" },
              }}
            >
              {isPlaying ? <PauseIcon fontSize="small" /> : <PlayArrowIcon fontSize="small" />}
            </IconButton>
            <Typography variant="body2" fontWeight={600}>
              {isPlaying ? "Đang phát lại..." : "Nghe lại đoạn ghi âm"}
            </Typography>
          </Box>

          <Box display="flex" alignItems="center" gap={1}>
            <Tooltip title="Xóa và ghi âm lại">
              <IconButton size="small" color="error" onClick={handleDeleteAudio}>
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      )}

      {/* Khi chưa ghi âm */}
      {!isRecording && !audioUrl && (
        <Box display="flex" alignItems="center" gap={1.5} mt={0.5}>
          <Button
            variant="outlined"
            size="small"
            color="primary"
            startIcon={<MicIcon />}
            onClick={startRecording}
            sx={{
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 700,
            }}
          >
            Nhấn để bắt đầu ghi âm
          </Button>
          <Typography variant="caption" color="text.secondary">
            Tiện lợi khi bạn ngại gõ chữ, tối đa 2 phút.
          </Typography>
        </Box>
      )}
    </Box>
  );
};
